// src/hooks/common/createRegisterCleaner.ts

import { useRouteCleanup } from "../hooks/common/useRouteCleanup";

/**
 * Props requeridas para crear un hook reutilizable de limpieza por ruta.
 */
interface CreateRegisterCleanerProps<T> {
  /**
   * Hook que retorna el store del módulo (por ejemplo, useBasePurchase).
   */
  useStoreHook: () => T;

  /**
   * Prefijo de ruta que identifica el módulo (por ejemplo, '/purchase-register').
   * Se usará con `startsWith()` para detectar coincidencia con rutas anidadas.
   */
  targetPathPrefix: string;

  /**
   * Nombre de la función dentro del store que debe ejecutarse para restablecer el estado.
   */
  resetFnName: keyof T;

  /**
   * (Opcional) Función para condicionar cuándo hacer la limpieza.
   * Por defecto, siempre limpia (`true`).
   */
  shouldClean?: (store: T) => boolean;
}

/**
 * Crea un hook de limpieza de estado al abandonar una ruta específica.
 *
 * ✅ Ejemplo de uso:
 * 
 * ```ts
 * export const usePurchaseRegisterCleaner = createRegisterCleaner({
 *   useStoreHook: useBasePurchase,
 *   targetPathPrefix: '/purchase-register',
 *   resetFnName: 'handlerResetBasePurchase',
 *   shouldClean: (store) => !store.purchaseLoaded && !store.purchaseSaved,
 * });
 * ```
 *
 * Este hook se puede ejecutar desde un componente global (como `RegisterWatchers`)
 * para mantener el estado limpio en toda la aplicación.
 */
export function createRegisterCleaner<T>({
  useStoreHook,
  targetPathPrefix,
  resetFnName,
  shouldClean = () => true,
}: CreateRegisterCleanerProps<T>) {
  return () => {
    const store = useStoreHook();

    // Extraemos el método de reseteo del store dinámicamente
    const resetFn = store[resetFnName] as unknown as () => void;

    // Ejecutamos el hook genérico de limpieza por ruta
    useRouteCleanup({
      matchFn: (path) => path.startsWith(targetPathPrefix),
      onLeave: resetFn,
      shouldClean: () => shouldClean(store),
    });
  };
}
